import { Customer } from 'types/customer';
import { EquipmentType } from 'types/damage';

/*
 * Typed outcome of a fleet unit lookup, mirroring the backend's new
 * GET /api/fleet-files/locations/{id}/lookup contract.
 *
 * The point of all of this is that the old hook returned `FleetFileUnit | null`, and `null` meant
 * every one of: the unit does not exist, the provider is down, the response was malformed, and you
 * are not allowed to ask. The gate screen showed the same "No fleet file found" for all four.
 */

/* Why part of a FOUND record could not be translated. Mirrors the backend enum exactly. */
export type FleetLookupIssueCode =
  | 'UNKNOWN_CUSTOMER_CODE'
  | 'CUSTOMER_UNMAPPED'
  | 'UNKNOWN_SIZE_TYPE_CODE'
  | 'SIZE_TYPE_UNMAPPED'
  | 'SIZE_TYPE_INACTIVE'
  | 'EQUIPMENT_TYPE_MISMATCH'
  | 'INVALID_DATE';

export interface FleetLookupIssue {
  code: FleetLookupIssueCode;
  field?: string;
  expected?: string;
  actual?: string;
  detail?: string;
  /* an admin's review note: the crosswalk deliberately suggests no size type for this code, and why */
  note?: string;
}

/* Stable provider error codes. Mirrors the backend enum exactly. */
export type FleetProviderErrorCode =
  | 'FLEET_INVALID_ENVELOPE'
  | 'FLEET_IDENTITY_MISMATCH'
  | 'FLEET_INVALID_CORE_DATA'
  | 'FLEET_UPSTREAM_AUTH_FAILED'
  | 'FLEET_UPSTREAM_UNAVAILABLE'
  | 'FLEET_UPSTREAM_TIMEOUT'
  | 'FLEET_INVALID_REQUEST';

/*
 * One account a fleet customer code may stand for at a location. Deliberately slim: the backend
 * sends only what is needed to check and choose, so several siblings do not each carry a full
 * customer's contacts, EDI recipients and report settings to the gate screen.
 */
export type FleetAccount = Pick<Customer, 'id' | 'code' | 'name'>;

/* The 200 body. A record that was found stays found even when `issues` is non-empty. */
export interface FleetLookupRecord {
  status: 'FOUND' | 'NOT_FOUND' | 'DISABLED';
  unitNumber: string;
  locationId: number;
  requestedEquipmentType?: EquipmentType;
  source?: { customerCode?: number; sizeTypeCode?: number };
  /*
   * The account to fill in — present only when the code stands for exactly one account. Never read
   * it for account checks; use `candidatesOf`, which knows when it may be trusted.
   */
  customer?: Customer;
  /* every account the fleet customer code may stand for here; absent only from an older backend */
  candidates?: FleetAccount[];
  /* canonical catalog code, or absent when unresolved — never a legacy string */
  sizeType?: string;
  sizeTypeLabel?: string;
  equipmentType?: EquipmentType;
  manufacturedDate?: string;
  fmcsaDate?: string;
  serialNumber?: string;
  licensePlate?: string;
  globalHuntFlag?: boolean;
  toBeSoldFlag?: boolean;
  revisions?: { customerMappingId?: number; sizeTypeMappingId?: number; customerMappingIds?: number[] };
  issues: FleetLookupIssue[];
  checkedAt: string;
}

/* The error body for a provider failure. */
export interface FleetErrorBody {
  status: number;
  errorCode: FleetProviderErrorCode;
  retryable: boolean;
  message?: string;
}

/*
 * The discriminated union every caller switches on.
 *
 * Deliberately has no `null` member. Making the compiler enumerate these is the point: the old
 * nullable return type let every call site treat a failure as "no record" without noticing.
 */
export type FleetLookupOutcome =
  | { kind: 'disabled' }
  /* provider holds a record; check `issues` before trusting any single field */
  | { kind: 'found'; record: FleetLookupRecord }
  /* provider genuinely holds none — a normal result, the operator enters it manually */
  | { kind: 'notFound'; record: FleetLookupRecord }
  /* provider unreachable or erroring; Retry is meaningful */
  | { kind: 'unavailable'; error: FleetErrorBody }
  /* provider did not answer in time; Retry is meaningful */
  | { kind: 'timeout'; error: FleetErrorBody }
  /* provider answered with something unusable; Retry is not meaningful */
  | { kind: 'invalidData'; error: FleetErrorBody }
  /* the caller may not ask — NEVER to be shown as "unmapped" or "not found" */
  | { kind: 'denied'; error?: FleetErrorBody }
  /* the request itself was wrong (e.g. blank unit number) */
  | { kind: 'invalidRequest'; error: FleetErrorBody };

/* True when showing a Retry control makes sense. */
export const isRetryable = (outcome: FleetLookupOutcome): boolean =>
  outcome.kind === 'unavailable' || outcome.kind === 'timeout';

/* True when the fleet integration failed, as opposed to answering. */
export const isFailure = (outcome: FleetLookupOutcome): boolean =>
  outcome.kind !== 'found' && outcome.kind !== 'notFound' && outcome.kind !== 'disabled';

export const hasIssue = (record: FleetLookupRecord, code: FleetLookupIssueCode): boolean =>
  record.issues?.some(issue => issue.code === code) ?? false;

/*
 * True when the record was found but its size type could not be resolved to a catalog code.
 *
 * This is the EXPECTED state for every mapping on release: the backend ships all catalog targets
 * null on purpose, because only 17 of 94 legacy strings exactly matched an active code and exact
 * equality is a candidate for review rather than evidence of correctness. The operator picks the
 * size type. Do not "fix" this by falling back to a legacy value.
 */
/*
 * The review note behind a deliberately empty suggestion (e.g. "Generic genset; select clip-on or
 * underslung"), when there is one. The gate shows it instead of the generic "not configured".
 */
export const sizeTypeReviewNote = (record: FleetLookupRecord): string | undefined =>
  record.issues?.find(issue => issue.code === 'SIZE_TYPE_UNMAPPED' && !!issue.note)?.note;

export const isSizeTypeUnresolved = (record: FleetLookupRecord): boolean =>
  hasIssue(record, 'SIZE_TYPE_UNMAPPED') ||
  hasIssue(record, 'SIZE_TYPE_INACTIVE') ||
  hasIssue(record, 'UNKNOWN_SIZE_TYPE_CODE');

/*
 * The accounts the fleet file allows for this record — the ONLY place account checks read from.
 *
 * `candidates` wins whenever it is present, even when empty: with several candidates the backend
 * leaves `customer` null on purpose, so falling back to it there would be wrong. The fallback exists
 * only for a backend that predates `candidates` (a REST rollback), where it reproduces the old
 * single-account behaviour instead of silently turning every check off.
 */
export const candidatesOf = (record: FleetLookupRecord): FleetAccount[] => {
  if (record.candidates) return record.candidates;
  if (!record.customer) return [];
  const { id, code, name } = record.customer;
  return [{ id, code, name }];
};

/* True when the account could not be resolved. Unresolved is NOT the same as verified-absent. */
export const isCustomerUnresolved = (record: FleetLookupRecord): boolean =>
  hasIssue(record, 'CUSTOMER_UNMAPPED') || hasIssue(record, 'UNKNOWN_CUSTOMER_CODE');
